import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/Dialog';
import { cn } from '@/lib/cn';

/**
 * "Put this consultation on hold" — the doctor pauses a patient who is waiting
 * for something (lab results, a scan, stepping out), sees other patients, and
 * resumes later. Nothing captured so far is lost; only the queue row's state
 * moves (backend/utils/consultationState.js holdConsultation).
 *
 * Any non-emergency reason makes it a PARKED hold (backend/utils/
 * queuePriority.js isParkedHold): the patient drops to the end of the
 * doctor's list and is never "next", so the queue keeps moving. Used by the
 * Queue screen and the Consultation screen.
 */

export const HOLD_REASONS = [
  'Awaiting lab results',
  'Awaiting scan / X-ray',
  'Awaiting other report',
  'Patient stepped out',
] as const;

export function HoldConsultationDialog({ open, patientName, nextPatientName, intro, cancelLabel = 'Cancel', defaultReason = HOLD_REASONS[0], pending = false, onConfirm, onCancel }: {
  open: boolean;
  patientName: string;
  /** A leading sentence, e.g. "Lab order placed." when asked right after ordering. */
  intro?: string;
  cancelLabel?: string;
  /** When set, the dialog says this patient will be seen straight after the hold. */
  nextPatientName?: string | null;
  defaultReason?: string;
  pending?: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState<string>(defaultReason);
  useEffect(() => { if (open) setReason(defaultReason); }, [open, defaultReason]);
  const trimmed = reason.trim();
  // 40 chars is queue.hold_reason's width; "Emergency…" would make it an
  // emergency hold, which has its own flow.
  const ok = trimmed.length >= 3 && trimmed.length <= 40 && !/^emergency/i.test(trimmed);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Put {patientName} on hold?</DialogTitle>
          <DialogDescription>
            {intro ? <><b>{intro}</b>{' '}</> : null}
            Everything recorded so far is kept. {patientName} moves to the end of the queue while waiting
            {nextPatientName ? <>, and you can see <b>{nextPatientName}</b> straight away</> : ', so you can see the next patient'}.
            Press <b>▶ Resume</b> when you are ready to continue.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-1.5">
          {HOLD_REASONS.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setReason(r)}
              className={cn(
                'rounded-full border px-2.5 py-1 text-[12px] transition-colors',
                reason === r ? 'border-teal bg-accent text-teal' : 'border-border text-ink-muted hover:border-teal hover:text-teal'
              )}
            >
              {r}
            </button>
          ))}
        </div>
        <FormField label="Waiting for" error={trimmed && !ok ? '3–40 characters' : undefined}>
          <Input value={reason} maxLength={40} onChange={(e) => setReason(e.target.value)} />
        </FormField>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} disabled={pending}>{cancelLabel}</Button>
          <Button disabled={!ok} loading={pending} onClick={() => onConfirm(trimmed)}>
            ⏸ Hold{nextPatientName ? ` & see ${nextPatientName}` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
